/* global JSX */
import { Box, Grid } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import * as appReportService from "../../../models/services/appReportService";
import { ISessionState } from "../../../models/session/sessionState";
import { AddButton } from "../../framework/Button/AddButton";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { ListCardField } from "../../framework/List/ListCardField";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageTitle } from "../../framework/Page/PageTitle";
import { ListSearchContainer } from "source/components/framework/List/ListSearchContainer";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { ListDetailsButton } from "source/components/framework/List/ListDetailsButton";
import { PageListActionContainer } from "source/components/framework/List/PageListActionContainer";
import { TrashBinButton } from "source/components/framework/Button/TrashBinButton";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { RestoreFromTrashBinButton } from "source/components/framework/Button/RestoreFromTrashBinButton/RestoreFromTrashBinButton";
import { EmptyTrashBinButton } from "source/components/framework/Button/EmptyTrashBinButton";
import { PreviewToggleButton } from "source/components/framework/Button/PreviewToggleButton";
import { getAppReportPageGeneralFilterFromFilterState, getAppReportPageGeneralFilterChange, IAppReportPageGeneralFilter, IAppReportPageFilterState } from "../AppReportPage/AppReportPageUtils";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";
import { IAppReportListItem } from "ui/Scripts/source/models/appReport/appReportListItem";
import { AppReportListStateCol } from "./AppReportListStateCol";
import { ModifyUserInfo } from "source/components/framework/User/ModifyUserInfo/ModifyUserInfo";
import { IPageNavMenuItem } from "source/components/framework/PageNav/PageNavMenuItems";
import { SettingsButton } from "source/components/framework/Button/SettingsButton";
import { AppReportNewDialog } from "../AppReportNewDialog";
import { IAppReportEditItem } from "ui/Scripts/source/models/appReport/appReportEditItem";
import { AppReportEditDialog } from "../AppReportEditDialog/AppReportEditDialog";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { AppReportListDetailsPanel } from "./AppReportListDetailsPanel";

interface IAppReportListProp extends IAppReportPageFilterState {
    sessionState: ISessionState;
    filterName: string;
    companyId: string;
    addRecord?: boolean;
    canCreateAppReports: boolean;
    listFilterIsSet: boolean;
    units: IIdTitleNode[];
    dataId?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    settingsActions: IPageNavMenuItem[];
    thrashBin: boolean;
    titleSuffix?: JSX.Element;
    reportTabs?: React.ReactNode;
    showDocumentPreview?: boolean;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onLoadPageData: () => void;
    onOpenFiltersDialog: () => void;
    onOpenTrashBinDialog: () => void;
    onChangeShowDocumentPreview?: (showDocumentPreview: boolean) => void;
    onToggleSettingsDialog: () => void;
}

interface IAppReportListSearchFilter extends IAppReportPageGeneralFilter {
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

//This class is specified only for dynamic property filtering
class AppReportListSearchFilter implements IAppReportListSearchFilter {
    unitIds: string[];
    stateIds: string[];
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
}

interface IAppReportListState extends IListUsingComponentState<IAppReportListItem>, IAppReportListSearchFilter {
    // List
    intialized: boolean;
    isLoading: boolean;

    listModeIsLine: boolean;
    checkedIds: string[];

    // Details
    detailsDataId: string;

    // Dialog
    showNewDialog: boolean;
    editDialogItem: IAppReportEditItem;
    editDialogUnits: IIdTitleNode[];
}

export class AppReportList extends React.Component<IAppReportListProp, IAppReportListState> {
    sentReadReceipts: string[] = [];

    constructor(props: IAppReportListProp) {
        super(props);
        const generalFilterValues = getAppReportPageGeneralFilterFromFilterState(props);
        this.state = {
            ...generalFilterValues,
            intialized: false,
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            pageSize: infiniteListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            listModeIsLine: true,
            items: [],
            selectedId: null,
            checkedIds: [],
            // Details
            detailsDataId: "",
            detailsItem: null,
            // Dialog
            showNewDialog: false,
            showEditDialog: false,
            editDialogId: "",
            editDialogItem: null,
            editDialogUnits: [],
            editDialogHasPrevious: false,
            editDialogHasNext: false,
        };
    }

    setReadReceipt = (detailsItem: IAppReportListItem) => {
        const props = this.props;
        if (!detailsItem || detailsItem.isRead) return;
        const userIsCreator = detailsItem.createUser.code === props.sessionState.code;
        if (userIsCreator || this.sentReadReceipts.indexOf(detailsItem.id) > -1) return;
        const result = AppUtils.callService(() => appReportService.sendAppReportReadReceipt(props.companyId, detailsItem.id), false, true);
        if (!result) return;
        this.sentReadReceipts.push(detailsItem.id);
        const items = this.state.items.slice(0);
        const item = items.find(i => i.id === detailsItem.id);
        if (!item) return;
        item.isRead = true;
        this.setState({
            items
        });
    };

    searchItems = async (searchFilter: IAppReportListSearchFilter, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setDialogData: boolean = true, addRecord: boolean = false, newSelectedId: string = null) => {
        const props = this.props;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => appReportService.getAppReportListItems(props.companyId, !refreshList ? searchFilter.pageSize : (searchFilter.page * searchFilter.pageSize), !refreshList ? searchFilter.page : 1,
            props.thrashBin, searchFilter.filter, null, null, searchFilter.unitIds, searchFilter.stateIds, searchFilter.sortColumn, searchFilter.sortOrderIsAsc), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(this.state.items, newSelectedId ?? this.state.selectedId, this.state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            ...searchFilter,
            intialized: true,
            page: !refreshList ? result.page : searchFilter.page,
            hasMore: result.hasMore,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogId: setDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : this.state.editDialogId,
            editDialogHasPrevious: setDialogData ? index > 0 : this.state.editDialogHasPrevious,
            editDialogHasNext: setDialogData ? (index < items.items.length - 1 || result.hasMore) : this.state.editDialogHasNext,
        }, () => {
            if (!addRecord) return;
            this.handleAdd();
        });
        this.props.onSetSortData(searchFilter.sortColumn, searchFilter.sortOrderIsAsc);
        this.setReadReceipt(detailsItem);
    };

    refreshList = async (setDialogData: boolean = true, newSelectedId: string = null) => {
        await this.searchItems(Base.filterPropertiesByClass(this.state, AppReportListSearchFilter), false, true, false, setDialogData, false, newSelectedId);
        if (!setDialogData) return;
        this.setState({ detailsDataId: Base.getGuid() });
    };

    componentDidMount(): void {
        this.searchItems(Base.filterPropertiesByClass(this.state, AppReportListSearchFilter), true, false, false, false, !!this.props.addRecord);
    }

    componentDidUpdate(prevProps: IAppReportListProp, prevState: IAppReportListState) {
        const props = this.props;
        const state = this.state;
        const prevDataId = prevProps.dataId ? prevProps.dataId : "";
        const dataId = props.dataId ? props.dataId : "";
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        const filterStateChange = getAppReportPageGeneralFilterChange(prevProps, props);
        const prevSortColumn = prevProps.sortColumn;
        const sortColumn = props.sortColumn;
        const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
        const sortOrderIsAsc = props.sortOrderIsAsc;
        if (dataId === prevDataId && companyId === prevCompanyId &&
            filterStateChange.notChanged &&
            prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
        this.searchItems({ ...filterStateChange, pageSize: state.pageSize, page: state.page, filter: state.filter, sortColumn, sortOrderIsAsc }, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems({ ...state, filter }, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems({ ...state, page: !nextPage ? 1 : state.page + 1, sortColumn, sortOrderIsAsc }, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        const detailsItem = this.state.items.find(i => i.id === selectedId);
        this.setState({
            selectedId,
            checkedIds,
            detailsItem
        });
        this.setReadReceipt(detailsItem);
    };

    handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    // #region New dialog
    handleAdd = () => {
        this.setState({
            showNewDialog: true
        });
    };

    handleAppReportNewDialogOk = (item: IAppReportEditItem, units: IIdTitleNode[]) => {
        this.setState({
            showNewDialog: false,
            showEditDialog: true,
            editDialogId: Base.emptyGuid,
            editDialogItem: item,
            editDialogUnits: units,
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            detailsItem: null
        });
    };

    handleAppReportNewDialogCancel = () => {
        this.setState({
            showNewDialog: false
        });
    };
    // #endregion Edit dialog

    // #region Edit dialog
    handleOpenLine = (id: string) => {
        this.setState(List.getShowEditDialogStateChange(this.state, id));
    };
    
    handleEditDialogSave = (id: string) => {
        this.refreshList(false, id);
        this.props.onLoadPageData();
    };

    handleEditDialogClose = () => {
        this.setState({
            showEditDialog: false,
            editDialogItem: null,
            editDialogUnits: [],
        });
        this.refreshList(false);
        this.props.onLoadPageData();
    };

    setEditDialogItemByIndex = (index: number) => {
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems({ ...this.state, page: this.state.page + 1 }, false, false, true)));
    };
    // #endregion Edit dialog

    // #region Print
    handlePrintDraft = async () => {
        const state = this.state;
        if (!state.selectedId) return;
        const fileName = await AppUtils.callService(() => appReportService.getAppReportDraftPrint(this.props.companyId, state.selectedId));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    handlePrintPublished = async () => {
        const state = this.state;
        if (!state.selectedId) return;
        const fileName = await AppUtils.callService(() => appReportService.getAppReportPublishedPrint(this.props.companyId, state.selectedId));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    handlePrint = async () => {
        const state = this.state;
        if (!state.selectedId) return;
        const item = this.state.items.find(i => i.id === state.selectedId);
        if (!item) return;
        if (item.canEdit) {
            await this.handlePrintDraft();
        } else {
            await this.handlePrintPublished();
        }
    };
    // #endregion Print

    handleEmptyTrashBin = async () => {
        const props = this.props;
        if (await AppUtils.showConfirmationDialog(Translations.EmptyReportTrashBinConfirmation) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const result = await AppUtils.callService(() => appReportService.emptyTrashBin(props.companyId));
        if (!result) return;
        this.refreshList();
    };

    handleRestoreFromTrashBin = async () => {
        const state = this.state;
        if (!state.selectedId) return;
        const result = await AppUtils.callService(() => appReportService.restoreAppReportFromTrashBin(this.props.companyId, state.selectedId, null));
        if (!result) return;
        this.refreshList(false);
    };

    getIntervalStr = (item: IAppReportListItem): string => {
        return (item.startDate ? Base.utcTimeToDateStr(item.startDate) : "") + (item.endDate ? " - " + Base.utcTimeToDateStr(item.endDate) : "");
    };

    render() {
        const props = this.props;
        const state = this.state;
        const beforeList = <>
            {!props.thrashBin &&
                <>
                    <PageListActionContainer
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <>
                                <ListActionButtons
                                    filterIsSet={props.listFilterIsSet}
                                    printDisabled={!state.selectedId}
                                    onFilter={props.onOpenFiltersDialog}
                                    onPrint={this.handlePrint}
                                />
                                <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                            </>
                        }
                    />
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    </ListSearchContainer>
                </>
            }
            {props.thrashBin &&
                <ActionContainer
                    leftItems={
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    }
                    rightItems={
                        <>
                            <Box display="flex" sx={{ marginRight: ".5rem" }}>
                                {props.sessionState.isCompanyAdmin &&
                                    <EmptyTrashBinButton onClick={this.handleEmptyTrashBin} disabled={state.items.length < 1} sx={{ marginRight: ".5rem" }}>
                                        {Translations.EmptyReportTrashBin}
                                    </EmptyTrashBinButton>
                                }
                                <RestoreFromTrashBinButton onClick={this.handleRestoreFromTrashBin} disabled={!state.detailsItem?.canEdit}>
                                    {Translations.RestoreReportFromTrashBin}
                                </RestoreFromTrashBinButton>
                            </Box>
                            <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                        </>
                    }
                />
            }
        </>;
        const list = <List
            intialized={state.intialized}
            isLoading={state.isLoading}
            hasMore={state.hasMore}
            globalLoadingInUse={true}
            height={!props.thrashBin ? "calc(100vh - 296px)" : "100%"}
            header={state.listModeIsLine
                ? {
                    columns: [
                        { size: 2, column: "unitCode", content: Translations.Unit },
                        { size: 1, column: "modifyDate", content: Translations.ModifyDate },
                        { size: 3, column: "state", content: Translations.State },
                        { size: 4, column: "code", content: Translations.Name },
                        { size: 2, column: "startDate", content: Translations.Interval },
                    ]
                }
                : null}
            lines={state.listModeIsLine
                ? state.items.map((item) => {
                    return {
                        columns: [
                            { size: 2, content: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.unitCode}</ListLinkValue> },
                            { size: 1, content: Base.timeToDateStr(item.modifyUser.date) },
                            { size: 3, content: <AppReportListStateCol item={item} /> },
                            { size: 4, content: item.code },
                            { size: 2, content: this.getIntervalStr(item) }
                        ],
                        id: item.id
                    };
                })
                : null
            }
            cards={!state.listModeIsLine
                ? state.items.map((item) => {
                    return {
                        sm: 4,
                        rows: [
                            {
                                cols: [
                                    {
                                        size: 6,
                                        title: Translations.Unit,
                                        value: <div><ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.unitCode}</ListLinkValue></div>
                                    },
                                    { size: 6, title: Translations.State, value: <AppReportListStateCol item={item} /> },
                                ]
                            },
                            {
                                cols: [
                                    { title: Translations.Name, value: item.code },
                                ]
                            },
                            {
                                cols: [
                                    { size: 6, title: Translations.Interval, value: this.getIntervalStr(item) },
                                    { size: 6, title: Translations.ModifyDate, value: Base.timeToDateStr(item.modifyUser.date) }
                                ]
                            }],
                        id: item.id
                    };
                })
                : null
            }
            lineDetails={state.detailsItem && !props.showDocumentPreview
                ? <Box position="relative">
                    <GridRow>
                        <Grid item sm={4}>
                            <GridRow>
                                <Grid item sm={6}>
                                    <ListCardField title={Translations.ModifyDate} value={Base.timeToDateStr(state.detailsItem.modifyUser.date)} />
                                </Grid>
                                <Grid item sm={6}>
                                    <ListCardField title={Translations.State} value={<AppReportListStateCol item={state.detailsItem} />} />
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.Name} value={state.detailsItem.code} />
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.Description} value={state.detailsItem.comment} />
                                </Grid>
                            </GridRow>
                        </Grid>
                        <Grid item sm={4}>
                            <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.Unit} value={state.detailsItem.unitCode} />
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.Interval} value={this.getIntervalStr(state.detailsItem)} />
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.Created} value={<ModifyUserInfo modifyUser={state.detailsItem.createUser} />} />
                                </Grid>
                            </GridRow>
                        </Grid>
                        <Grid item sm={4}>
                            <GridRow>
                                <Grid item sm={12}>
                                    <ListCardField title={Translations.Modified} value={<ModifyUserInfo modifyUser={state.detailsItem.modifyUser} />} />
                                </Grid>
                            </GridRow>
                            {!!state.detailsItem.publishUser &&
                                <GridRow>
                                    <Grid item sm={12}>
                                        <ListCardField title={Translations.Published} value={<ModifyUserInfo modifyUser={state.detailsItem.publishUser} />} />
                                    </Grid>
                                </GridRow>
                            }
                            {!!state.detailsItem.archiveUser &&
                                <GridRow>
                                    <Grid item sm={12}>
                                        <ListCardField title={Translations.Archived} value={<ModifyUserInfo modifyUser={state.detailsItem.archiveUser} />} />
                                    </Grid>
                                </GridRow>
                            }
                        </Grid>
                    </GridRow>
                    <ListDetailsButton
                        onClick={() => this.handleOpenLine(state.selectedId)}
                    />
                </Box>
                : null}
            sortColumn={state.sortColumn}
            sortOrderIsAsc={state.sortOrderIsAsc}
            selectedId={state.selectedId}
            checkedIds={state.checkedIds}
            onSetSelectedIds={this.handleSetSelectedIds}
            onSearchLines={this.handleSearchLines}
            onOpenLine={this.handleOpenLine}
        />;

        return (
            <>
                <div style={{ width: "100%", position: "relative", flexBasis: 0, flexGrow: 7, display: "flex" }}>
                    {!props.thrashBin &&
                        <PageTitle title={Translations.Reports + (props.filterName ? " - " + props.filterName : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))} position="absolute">
                            {props.settingsActions.length > 0 &&
                                <SettingsButton onClick={props.onToggleSettingsDialog} sx={{ marginRight: "24px" }}>
                                    {Translations.Settings}
                                </SettingsButton>
                            }
                            {props.canCreateAppReports &&
                                <TrashBinButton onClick={props.onOpenTrashBinDialog}>
                                    {Translations.TrashBin}
                                </TrashBinButton>
                            }
                            {props.canCreateAppReports &&
                                <AddButton onClick={this.handleAdd} sx={{ marginRight: "24px" }}>
                                    {Translations.AddReport}
                                </AddButton>
                            }
                            <PreviewToggleButton
                                value="preview"
                                selected={props.showDocumentPreview}
                                onChange={() => props.onChangeShowDocumentPreview(!props.showDocumentPreview)}
                                style={{ padding: "7px" }}
                            />
                        </PageTitle>
                    }
                    {(!props.thrashBin
                        ? <MainListContainer sx={{ width: "100%" }}>
                            {beforeList}
                            {list}
                        </MainListContainer>
                        : <VerticalContainer
                            width="100%"
                            before={beforeList}
                            main={list}
                        />
                    )}
                    {state.showNewDialog &&
                        <AppReportNewDialog
                            companyId={props.companyId}
                            onOk={this.handleAppReportNewDialogOk}
                            onCancel={this.handleAppReportNewDialogCancel}
                        />
                    }
                    {(state.showEditDialog && !!state.editDialogId) &&
                        <AppReportEditDialog
                            companyId={props.companyId}
                            sessionState={props.sessionState}
                            appReportId={state.editDialogId}
                            newAppReport={state.editDialogItem}
                            newAppReportUnits={state.editDialogUnits}
                            hasPrevious={state.editDialogHasPrevious}
                            hasNext={state.editDialogHasNext}
                            onSaved={this.handleEditDialogSave}
                            onClose={this.handleEditDialogClose}
                            onPrint={this.handlePrint}
                            onRefreshList={() => this.refreshList(false)}
                            onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                            onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        />
                    }
                </div>
                {props.showDocumentPreview &&
                    <AppReportListDetailsPanel
                        sessionState={props.sessionState}
                        companyId={props.companyId}
                        appReportId={state.selectedId}
                        editDialogVisible={state.showEditDialog}
                        dataId={state.detailsDataId}
                        onPrintDraft={this.handlePrintDraft}
                        onPrintPublished={this.handlePrintPublished}
                        onClose={() => props.onChangeShowDocumentPreview(false)}
                    />
                }
            </>
        );
    }
}